// ready-time-select.component.ts
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import moment from 'moment-timezone';
import { formatDate } from '@angular/common';

interface Order {
  earliestReadyTimestamp?: Date;
  orderReadyTimestamp?: Date;
  isASAP?: boolean;
  location?: {
    isOpen?: boolean;
    supportsAsapOrders?: boolean;
    supportsAdvancedOrders?: boolean;
    timezone?: string;
    utcOffset?: string | number;
  };
}

@Component({
  selector: 'app-ready-time-select',
  templateUrl: './ready-time-select.component.html',
  styleUrls: ['./ready-time-select.component.scss'],
})
export class ReadyTimeSelectComponent implements OnChanges, OnInit {
  @Input() order: Order = null;
  @Input() isAsap = true;
  @Input() availableDates: Date[] = [];

  @Input()
  set availableTimes(val: Date[]) {
    this.actualTimes = val ?? [];
    this.updateButtonTimes();
  }

  @Input() dateIndex = 0;
  @Input() timeIndex = 0;
  @Input() loadingTimeMode: string = null;
  @Output() asapClick = new EventEmitter<void>();
  @Output() dateChange = new EventEmitter<Date>();
  @Output() timeChange = new EventEmitter<Date>();

  actualTimes: Date[] = [];
  dateTimeForm: FormGroup;
  buttonTimes: Date[] = []; // First 4 times to show as buttons
  dropdownTimes: Date[] = []; // All remaining times for dropdown
  showDateDropdown = false;
  showTimeDropdown = false;

  dateLoading = false;
  timeLoading = false;

  selectedDate: Date = null;
  selectedTime: Date = null;
  today = moment().startOf('day').toDate();

  dropdownDateSelection: string = '';
  dropdownTimeSelection: string = '';

  dateSelectElement: HTMLSelectElement = null;
  timeSelectElement: HTMLSelectElement = null;

  constructor(private fb: FormBuilder) {
    this.dateTimeForm = this.fb.group({
      selectedDate: [null],
      selectedTime: [null],
    });
  }

  ngOnInit() {
    if (this.order) {
      if (this.order.orderReadyTimestamp) {
        this.selectedDate = this.order.orderReadyTimestamp;
        this.selectedTime = this.order.orderReadyTimestamp;
        this.dateTimeForm.get('selectedDate').setValue(this.order.orderReadyTimestamp, { emitEvent: false });
        this.dateTimeForm.get('selectedTime').setValue(this.order.orderReadyTimestamp, { emitEvent: false });
      } else if (this.actualTimes.length > 0) {
        this.selectedDate = this.actualTimes[0];
        this.dateTimeForm.get('selectedDate').setValue(this.actualTimes[0], { emitEvent: false });
      }
    }

    // Subscribe to form value changes - but only when user explicitly changes the value
    this.dateTimeForm.get('selectedDate').valueChanges.subscribe(date => {
      if (date && !this.dateLoading) {
        this.onDateChange(date);
      }
    });

    this.dateTimeForm.get('selectedTime').valueChanges.subscribe(time => {
      if (time && !this.timeLoading) {
        this.onTimeChange(time);
      }
    });

    this.updateButtonTimes();

    setTimeout(() => {
      this.dateSelectElement = document.querySelector('#date-select') as HTMLSelectElement;
      this.timeSelectElement = document.querySelector('#time-select') as HTMLSelectElement;
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['order']) {
      this.timeLoading = false;
      this.dateLoading = false;

      if (this.order?.orderReadyTimestamp) {
        this.selectedDate = this.order.orderReadyTimestamp;
        this.selectedTime = this.order.orderReadyTimestamp;
        this.dateTimeForm.get('selectedDate').setValue(this.order.orderReadyTimestamp, { emitEvent: false });
        this.dateTimeForm.get('selectedTime').setValue(this.order.orderReadyTimestamp, { emitEvent: false });
      }
    }

    if (changes['availableTimes']) {
      this.dateLoading = false;
      this.updateButtonTimes();
      if (this.actualTimes.length > 0 && !this.selectedTime) {
        this.dateTimeForm.get('selectedTime').setValue(this.actualTimes[0], { emitEvent: false });
      }
    }

    if (changes['loadingTimeMode']) {
      if (this.loadingTimeMode === null) {
        this.timeLoading = false;
        this.dateLoading = false;
      }
    }

    if (changes['dateIndex'] && !changes['dateIndex'].firstChange) {
      this.clearDateDropdownSelection();
    }
    
    // If timeIndex changes, clear the time dropdown selection
    if (changes['timeIndex'] && !changes['timeIndex'].firstChange) {
      this.clearTimeDropdownSelection();
    }
  }

  updateButtonTimes() {
    // Set first 4 times as button times, rest as dropdown times
    if (this.actualTimes.length > 0) {
      this.buttonTimes = this.actualTimes.slice(0, 4);
      this.dropdownTimes = this.actualTimes.slice(4);
    } else {
      this.buttonTimes = [];
      this.dropdownTimes = [];
    }
  }

  clearDateDropdownSelection() {
    // Reset the model
    this.dropdownDateSelection = '';
    
    // Reset the actual DOM element if it exists
    if (this.dateSelectElement) {
      this.dateSelectElement.value = '';
    } else {
      // If the element isn't available yet, try to find it
      const selectElement = document.querySelector('#date-select') as HTMLSelectElement;
      if (selectElement) {
        selectElement.value = '';
        this.dateSelectElement = selectElement;
      }
    }
  }

  clearTimeDropdownSelection() {
    // Reset the model
    this.dropdownTimeSelection = '';
    
    // Reset the actual DOM element if it exists
    if (this.timeSelectElement) {
      this.timeSelectElement.value = '';
    } else {
      // If the element isn't available yet, try to find it
      const selectElement = document.querySelector('#time-select') as HTMLSelectElement;
      if (selectElement) {
        selectElement.value = '';
        this.timeSelectElement = selectElement;
      }
    }
  }

  // 2. Modify the onDateSelectChange method
  onDateSelectChange(event: any) {
    if (this.dateLoading || !event.target.value) return;

    const selectedDate = new Date(event.target.value);
    this.onDateButtonClick(selectedDate);

    // Store current selection for display purposes
    this.dropdownDateSelection = event.target.value;

    // Reset time dropdown selection - this is the key change you requested
    this.clearTimeDropdownSelection();
    this.selectedTime = null;
    this.timeIndex = 0;

    // Reset visual selection ONLY when user opens dropdown again
    event.target.addEventListener(
      'focus',
      () => {
        event.target.value = '';
      },
      { once: true }
    );
  }

  onTimeSelectChange(event: any) {
    if (this.timeLoading || !event.target.value) return;

    const selectedTime = new Date(event.target.value);
    this.onTimeButtonClick(selectedTime);

    // Store current selection for display purposes
    this.dropdownTimeSelection = event.target.value;

    // Reset visual selection ONLY when user opens dropdown again
    event.target.addEventListener(
      'focus',
      () => {
        event.target.value = '';
      },
      { once: true }
    );
  }

  getTimeButtonsLimit(): number {
    const showAsap =
      this.order?.location?.supportsAsapOrders &&
      this.order?.location?.isOpen &&
      (!this.selectedDate || (this.selectedDate && moment(this.selectedDate).isSame(moment(this.order.earliestReadyTimestamp), 'day'))) &&
      moment(this.order.earliestReadyTimestamp).isSame(moment(this.today), 'day');

    return showAsap ? 3 : 4; // Show 3 time slots + ASAP or 4 time slots
  }

  getTimeButtonsToShow(): Date[] {
    const limit = this.getTimeButtonsLimit();
    return this.actualTimes.slice(0, limit);
  }

  onDateButtonClick(date: Date) {
    if (this.dateLoading) return;
    
    // Clear the date dropdown selection when clicking date button
    this.clearDateDropdownSelection();
    
    this.selectedDate = date;
    this.dateTimeForm.get('selectedDate').setValue(date, { emitEvent: false });
    
    // Clear the time selection when date changes
    this.selectedTime = null;
    this.clearTimeDropdownSelection();
    this.timeIndex = 0;
    this.dateTimeForm.get('selectedTime').setValue(null, { emitEvent: false });
    
    this.onDateChange(date);
  }

  onTimeButtonClick(time: Date) {
    if (this.timeLoading) return;

    this.clearTimeDropdownSelection();

    this.selectedTime = time;
    this.dateTimeForm.get('selectedTime').setValue(time, { emitEvent: false });
    this.onTimeChange(time);
  }

  isDateSelected(date: Date): boolean {
    if (!this.selectedDate || !date) return false;
    return moment(date).isSame(moment(this.selectedDate), 'day');
  }

  isTimeSelected(time: Date): boolean {
    if (!this.selectedTime || !time) return false;
    return moment(time).isSame(moment(this.selectedTime), 'minute');
  }

  asapClicked() {
    // Clear both date and time selections
    this.selectedDate = null;
    this.selectedTime = null;
    this.clearDateDropdownSelection();
    this.clearTimeDropdownSelection();
    
    this.dateTimeForm.get('selectedDate').setValue(null);
    this.dateTimeForm.get('selectedTime').setValue(null);
    this.timeLoading = true;
    this.dateLoading = true;
    this.asapClick.emit();
  }

  onDateChange(date: Date) {
    this.selectedDate = date;
    this.dateLoading = true;
    
    // Reset time index when date changes
    this.timeIndex = 0;
    
    this.dateChange.emit(date);
  }

  onTimeChange(time: Date) {
    this.selectedTime = time;
    this.timeLoading = true;
    this.timeChange.emit(time);
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return moment(date1).isSame(moment(date2), 'day');
  }

  // Format date for display in the dropdown
  formatShortDate(date: Date): string {
    if (!date) return '';
    const offset = Number(this.order?.location?.utcOffset || 0);
    return moment(date).utcOffset(offset).format('M/D/YY');
  }

  isToday(date: Date): boolean {
    if (!date) return false;
    const offset = Number(this.order?.location?.utcOffset || 0);
    return moment(date).utcOffset(offset).isSame(moment().utcOffset(offset), 'day');
  }

  formatWeekday(date: Date): string {
    if (!date) return '';
    const offset = Number(this.order?.location?.utcOffset || 0);
    return moment(date).utcOffset(offset).format('ddd');
  }

  // Format time for display in the dropdown
  formatTime(time: Date): string {
    if (!time) return '';
    const offset = Number(this.order?.location?.utcOffset || 0);
    return moment(time).utcOffset(offset).format('h:mm a');
  }

  // Helper method to compare dates for selected option in select
  compareWithDate(date1: any, date2: any): boolean {
    if (!date1 || !date2) return false;
    return this.isSameDay(new Date(date1), new Date(date2));
  }

  // Helper method to compare times for selected option in select
  compareWithTime(time1: any, time2: any): boolean {
    if (!time1 || !time2) return false;
    return moment(time1).isSame(moment(time2), 'minute');
  }

  availableTimeTracker(index: number, item: Date) {
    return `${index}${item.toISOString()}`;
  }

  availableDateTracker(index: number, item: Date) {
    return `${index}${item.toISOString()}`;
  }

  protected readonly formatDate = formatDate;
}
